import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import styles from "./dashboards-page.module.scss";
import Spinner from "@src/components/atoms/spinner/spinner";
import { DashboardCanvasHeader } from "@src/components/molecules/dashboard-canvas-header/dashboard-canvas-header";
import { DashboardTileGrid } from "@src/components/molecules/dashboard-tile-grid/dashboard-tile-grid";
import { EmptyDashboardView } from "@src/components/molecules/empty-dashboard-view/empty-dashboard-view";
import { ViewerPlaceholder } from "@src/components/molecules/viewer-placeholder/viewer-placeholder";
import { useViewerDashboard } from "./hooks/use-viewer-dashboard";
import { useErrorSnackbar } from "@src/hooks/use-error-snackbar";
import { paletteColorsToStyle } from "@components/molecules/dashboard-theme-editor/palette-style";
import { useI18N } from "@src/language/language-context";

const cssClassName = "dashboards-page";

/** Read-only public-facing viewer.
 *
 *  Lives at `/dashboard` (route `Route.DASHBOARD`) and
 *  `/dashboard/:dashboardId` (see `routes/dashboard-paths.ts`). The plain URL
 *  loads the dashboard the server flags as default; the URL with an id loads
 *  that dashboard. Both go through `useViewerDashboard`, which owns all the
 *  fetching and shares no state with the builder (`/dashboard-builder`). The
 *  read-only contract is enforced structurally:
 *
 *    - `useViewerDashboard` performs no writes and never reads the
 *      shared `dashboards` slice, so the builder's selection and
 *      list responses cannot reach this page.
 *    - The viewer does NOT import `useAutoPromoteDefault` either, so
 *      "viewer never writes" is a property of the import graph, not
 *      a per-call-site flag.
 *    - Its two "nothing to show" messages are its own component
 *      (`ViewerPlaceholder`), so the page never tells the reader to
 *      create or select a dashboard they cannot create or select.
 *    - `<DashboardCanvasHeader readOnly>` — no publish button.
 *    - `<DashboardTileGrid readOnly>` — no per-tile menu, and
 *      Gridstack is initialised with `staticGrid: true` to lock
 *      drag/resize.
 *
 *  Reuses the editor's `dashboards-page.module.scss` because
 *  `__central-panel { flex: 1; min-width: 0 }` fills the row when
 *  its siblings are absent, and the user already understands the
 *  gradient-border frame from the editor (visual continuity). */
export const DashboardViewerPage = () => {
  const i18n = useI18N();
  const { dashboardId } = useParams<{ dashboardId?: string }>();
  // The viewer owns its own data. With an id in the URL it fetches that
  // dashboard directly; without one it loads the configured default. It shares
  // nothing with the editor, so the editor's selection cannot leak in and a
  // later list response cannot replace the dashboard being shown. Read-only is
  // structural — this hook performs no writes, and the editor-only
  // auto-promote hook is not in this import graph.
  const viewerState = useViewerDashboard(dashboardId);

  const dashboard =
    viewerState.status === "ready" ? viewerState.dashboard : null;

  // Both failure paths (the list GET, and a dashboard's detail GET) toast with
  // the message the backend returned, which `use-viewer-dashboard` takes from
  // `parseAxiosApiError`. The canvas shows the translated generic copy instead.
  // Non-error statuses carry no message, so nothing toasts for them.
  useErrorSnackbar(viewerState.status === "error" ? viewerState.error : null);

  // Same theming pipeline as the editor so themed
  // dashboards cascade colours into chart widgets. The anchor
  // div also lets `useThemeColorsForCharts` bind to the central
  // panel via `[data-theme-anchor]` (documented in the hook).
  const paletteStyle = useMemo(
    () =>
      dashboard
        ? paletteColorsToStyle(dashboard.presentation?.colors)
        : undefined,
    [dashboard],
  );

  // One place to decide what the canvas shows. `ready` is the only state that
  // has a dashboard in hand, so the grid never renders a not-yet-loaded one.
  const renderCanvas = () => {
    if (viewerState.status === "loading") {
      return (
        <div className={styles[`${cssClassName}__spinner-container`]}>
          <Spinner />
        </div>
      );
    }
    if (viewerState.status === "error") {
      return <EmptyDashboardView variant="error" />;
    }
    if (viewerState.status === "no-dashboards") {
      // Nothing was ever created. Not the editor's "create or select" copy:
      // this page has no dashboard list and no create action.
      return <ViewerPlaceholder variant="no-dashboards" />;
    }
    if (viewerState.status === "no-default") {
      return <ViewerPlaceholder variant="no-default" />;
    }
    if (viewerState.status === "draft") {
      // A hand-written URL named an unpublished dashboard. The link list only
      // ever contains published ids, so this is a guard, not a normal path.
      return <ViewerPlaceholder variant="draft" />;
    }

    const { dashboard: readyDashboard } = viewerState;
    return Object.keys(readyDashboard.widgets).length > 0 ? (
      <DashboardTileGrid dashboard={readyDashboard} readOnly />
    ) : (
      // Inline read-only "no widgets yet" message rather
      // than reusing `EmptyDashboardView` because the
      // editor's variant carries an "Add first widget"
      // button that opens the picker modal — useless
      // and confusing on a viewer that can't edit. Reuse
      // the editor's i18n key for the copy so we don't
      // add new strings.
      <div className={styles[`${cssClassName}__empty-readonly`]}>
        {i18n("pages.dashboardsPage.emptyDashboard")}
      </div>
    );
  };

  return (
    <MainContainer>
      <RoundBorderComponentContainer>
        <div className={styles[cssClassName]}>
          {/* Reuse the editor's `__inner-container` flexbox. With
            the left + right columns stripped out, `__central-panel`
            fills 100% of the row. */}
          <div className={styles[`${cssClassName}__inner-container`]}>
            <div
              className={styles[`${cssClassName}__central-panel`]}
              style={paletteStyle}
              data-theme-anchor=""
            >
              {/* Read-only header: no publish button even on draft. */}
              {dashboard && (
                <DashboardCanvasHeader dashboard={dashboard} readOnly />
              )}
              <div className={styles[`${cssClassName}__canvas`]}>
                {renderCanvas()}
              </div>
              {/* No DashboardThemeEditor on the viewer. Themes
                flow in through `paletteStyle` + CSS vars; the
                viewer advance them as is. */}
            </div>
          </div>
        </div>
      </RoundBorderComponentContainer>
    </MainContainer>
  );
};
